/* eslint-disable camelcase */
import { getUrlQueryParams, removeQueryParams } from '../../utils/url';
import { REFERRER_KEYS } from './constants';
import {
  TrackProduct,
  Product,
  ProductContentList,
  ConfirmationProps,
  CheckoutProps,
  CustomerName,
  ProductListViewedProps,
  SpendDetails,
  IAnalytics,
  PageContent,
  SegmentProduct,
} from './types';

export function getUserTraits(): object {
  if (!window.analytics || !window.analytics.user) {
    return {};
  }

  const user = window.analytics.user();
  if (!user || !user.traits) {
    return {};
  }

  return user.traits();
}

function getSegment() {
  return {
    track: (name: string, props: object, options?: object) => window.analytics.track(name, props, { ...options, traits: getUserTraits() }),
    page: window.analytics.page,
    identify: window.analytics.identify,
  };
}

export const SegmentWrapper: IAnalytics = {
  elementViewed: (name: string) => {
    getSegment().track('Element Viewed', { name });
  },

  elementClicked: (name: string) => {
    getSegment().track('Element Clicked', { name });
  },

  trackEvent: (eventName: string, props: any) => {
    getSegment().track(eventName, { ...props });
  },

  trackError: (props: {caller: string, error: any}) => {
    getSegment().track('Internal Error', { ...props });
  },

  pageViewed: (details?: PageContent) => {
    getSegment().page(details?.name, { googleOptimize: details?.optimizeProperties || [], ...getUrlQueryParams() });
    removeQueryParams(REFERRER_KEYS);
  },

  productListViewed: ({ originListID, productList, category }: ProductListViewedProps) => {
    getSegment().track('Product List Viewed', {
      list_id: originListID,
      category,
      products: formatProductList(productList),
    });
  },

  productViewed: (product: SegmentProduct) => {
    const {
      event_id = null, externalId = null, fbc, fbp, ...rest
    } = product;
    getSegment().track('Product Viewed', {
      ...formatProduct(rest),
      externalId,
      eventId: event_id,
      fbc,
      fbp,
    });
  },

  productClicked: (product: Product) => {
    getSegment().track('Product Clicked', formatProduct(product));
  },

  productAdded: (product: SegmentProduct) => {
    const {
      event_id = null, externalId = null, fbc, fbp, ...rest
    } = product;
    getSegment().track('Product Added', {
      ...formatProduct(rest),
      externalId,
      eventId: event_id,
      fbc,
      fbp,
    });
  },

  productRemoved: (product: SegmentProduct) => {
    const { event_id = null, externalId = null, ...rest } = product;
    getSegment().track('Product Removed', {
      ...formatProduct(rest),
      event_id,
      externalId,
    });
  },

  // Coupons
  couponEntered: (coupon: string) => {
    getSegment().track('Coupon Entered', {
      coupon_id: coupon,
    });
  },

  couponApplied: (coupon: string) => {
    getSegment().track('Coupon Applied', {
      coupon_id: coupon,
    });
  },

  couponDenied: (coupon: string) => {
    getSegment().track('Coupon Denied', {
      coupon_id: coupon,
    });
  },

  couponRemoved: (coupon: string) => {
    getSegment().track('Coupon Removed', {
      coupon_id: coupon,
    });
  },

  // Campaigns
  campaignViewed: (data: object) => {
    getSegment().track('Campaign Viewed', data);
  },
  campaignSubscribed: (data: object) => {
    getSegment().track('Campaign Subscribed', data);
  },
  campaignDismissed: (data: object) => {
    getSegment().track('Campaign Dismissed', data);
  },

  // Identifications
  UIDIdentified: (uid: string) => {
    getSegment().identify(uid);
  },

  emailIdentified: (email: string) => {
    getSegment().identify({
      email,
    });
  },

  nameIdentified: ({ firstName, lastName }: CustomerName) => {
    getSegment().identify({
      firstName,
      lastName,
    });
  },

  phoneNumberIdentified: (phone: string) => {
    getSegment().identify({
      phone,
    });
  },

  // Cart
  cartViewed: (products: ProductContentList) => {
    getSegment().track('Cart Viewed', {
      products: formatProductList(products),
    });
  },

  cartAbandoned: (products: ProductContentList) => {
    getSegment().track('Cart Abandoned', {
      products: formatProductList(products),
    });
  },

  checkoutStarted: ({
    value, revenue, tax, shipping, discount, coupon, products, event_id, externalId, fbc, fbp,
  }: CheckoutProps) => {
    getSegment().track('Checkout Started', {
      externalId,
      eventId: event_id,
      fbc,
      fbp,
      value: value / 100,
      revenue: revenue / 100,
      currency: 'USD',
      coupon,
      tax: tax / 100,
      shipping: shipping / 100,
      discount: discount / 100,
      products: formatProductList(products),
    });
  },

  orderCompleted: (confirmation: ConfirmationProps) => {
    const costBreakdown = confirmation.order.costBreakdown;

    getSegment().track('Order Completed', {
      coupon: costBreakdown.couponName,
      currency: 'USD',
      discount: costBreakdown.discountAmount / 100,
      order_id: confirmation.orderNumber,
      payment_method: confirmation.method,
      revenue: costBreakdown.totalCost / 100,
      shipping: costBreakdown.productShipping / 100,
      subtotal: (costBreakdown.productCost - (costBreakdown?.discountAmount || 0)) / 100,
      tax: costBreakdown.productTax / 100,
      total: costBreakdown.productCost / 100,
      products: formatProductList(confirmation.products),
      is_package: confirmation.packageInCart,
    });
  },

  marketingSpendUpdated: (details: SpendDetails) => {
    getSegment().track('Marketing Spend Updated', details);
  },

  // blog
  adClicked: (details: object) => {
    getSegment().track('Ad Clicked', details);
  },

  // customer dashboard
  reactivateUser: (email: string) => {
    getSegment().track('Customer Dashboard | Reactivation', { email });
  },
};

// TODO remove check for categor, title, img_url. This was added to support the old product schema https://github.com/fightcamp/fightcamp-web-monorepo/pull/1098/files/7b7d887bcbf4f906eea3ab1007de25692a6c32a4#r1761745909
function formatProduct(product: SegmentProduct): TrackProduct {
  return {
    product_id: product?.id,
    category: product?.productType ?? product?.category,
    name: product?.productName ?? product?.title,
    brand: 'FightCamp',
    price: product?.price / 100,
    value: product?.price / 100,
    currency: 'USD',
    quantity: 1,
    url: `https://${window.location.hostname}${product.pathId}`,
    img_url: product?.img_url || undefined,
    variant: product?.variantId || null,
    coupon: null,
    sku: null,
  };
}

function formatProductList(productList: ProductContentList) {
  return productList.map((product, pos) => ({
    ...formatProduct(product),
    position: pos + 1,
  }));
}
